import React, { useState } from 'react';
import './SolutionCategories.css';

export interface CategoryData {
  name: string;
  count: number;
  subcategories?: CategoryData[];
}

export interface SolutionCategoriesProps {
  categories: CategoryData[];
  selectedCategories: string[];
  loading?: boolean;
  error?: string | null;
  showCounts?: boolean;
  multiSelect?: boolean;
  showSubcategories?: boolean;
  showClearAll?: boolean;
  layout?: 'sidebar' | 'dropdown' | 'horizontal';
  onCategoryChange: (categories: string[]) => void;
  onClearAll?: () => void;
  className?: string;
}

const SolutionCategories: React.FC<SolutionCategoriesProps> = ({
  categories,
  selectedCategories,
  loading = false,
  error = null,
  showCounts = true,
  multiSelect = true,
  showSubcategories = false,
  showClearAll = true,
  layout = 'sidebar',
  onCategoryChange,
  onClearAll,
  className = '',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  // Handle category selection
  const handleCategoryClick = (categoryName: string) => {
    let newSelectedCategories: string[];

    if (multiSelect) {
      if (selectedCategories.includes(categoryName)) {
        // Remove category
        newSelectedCategories = selectedCategories.filter(cat => cat !== categoryName);
      } else {
        // Add category
        newSelectedCategories = [...selectedCategories, categoryName];
      }
    } else {
      // Single select - toggle or select
      newSelectedCategories = selectedCategories.includes(categoryName) ? [] : [categoryName];
    }

    onCategoryChange(newSelectedCategories);
  };

  // Handle clear all
  const handleClearAll = () => {
    onCategoryChange([]);
    if (onClearAll) {
      onClearAll();
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryName: string) => {
    if (expandedCategories.includes(categoryName)) {
      setExpandedCategories(expandedCategories.filter(cat => cat !== categoryName));
    } else {
      setExpandedCategories([...expandedCategories, categoryName]);
    }
  };

  // Get total selected count
  const selectedCount = selectedCategories.length;

  // Get total solutions count
  const totalCount = categories.reduce((sum, cat) => sum + cat.count, 0);

  // Render category item
  const renderCategoryItem = (category: CategoryData, isSubcategory = false) => {
    const isSelected = selectedCategories.includes(category.name);
    const isExpanded = expandedCategories.includes(category.name);
    const hasSubcategories = showSubcategories && category.subcategories && category.subcategories.length > 0;

    return (
      <div key={category.name} className={`solution-categories__item ${isSubcategory ? 'solution-categories__item--subcategory' : ''}`}>
        <div className="solution-categories__item-content">
          <button
            className={`solution-categories__category ${isSelected ? 'solution-categories__category--selected' : ''}`}
            onClick={() => handleCategoryClick(category.name)}
            disabled={loading}
          >
            <span className="solution-categories__category-name">
              {category.name}
            </span>
            {showCounts && (
              <span className="solution-categories__category-count">
                ({category.count})
              </span>
            )}
            {isSelected && (
              <svg
                className="solution-categories__check-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20,6 9,17 4,12" />
              </svg>
            )}
          </button>

          {hasSubcategories && (
            <button
              className="solution-categories__expand-button"
              onClick={() => toggleCategoryExpansion(category.name)}
              aria-label={isExpanded ? 'Collapse subcategories' : 'Expand subcategories'}
            >
              <svg
                className={`solution-categories__expand-icon ${isExpanded ? 'solution-categories__expand-icon--expanded' : ''}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>
          )}
        </div>

        {/* Subcategories */}
        {hasSubcategories && isExpanded && (
          <div className="solution-categories__subcategories">
            {category.subcategories!.map(subcategory => 
              renderCategoryItem(subcategory, true)
            )}
          </div>
        )}
      </div>
    );
  };

  // Render dropdown trigger
  const renderDropdownTrigger = () => (
    <button
      className="solution-categories__dropdown-trigger"
      onClick={toggleDropdown}
      disabled={loading}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3" />
      </svg>
      <span>
        Categories
        {selectedCount > 0 && (
          <span className="solution-categories__selected-badge">
            {selectedCount}
          </span>
        )}
      </span>
      <svg
        className={`solution-categories__dropdown-icon ${isDropdownOpen ? 'solution-categories__dropdown-icon--open' : ''}`}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="6,9 12,15 18,9" />
      </svg>
    </button>
  );

  // Render categories list
  const renderCategoriesList = () => (
    <div className="solution-categories__list">
      {loading ? (
        // Loading state
        <div className="solution-categories__loading">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="solution-categories__loading-item">
              <div className="solution-categories__loading-name"></div>
              <div className="solution-categories__loading-count"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        // Error state
        <div className="solution-categories__error">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p>Failed to load categories</p>
        </div>
      ) : categories.length === 0 ? (
        // Empty state
        <div className="solution-categories__empty">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3" />
          </svg>
          <p>No categories available</p>
        </div>
      ) : (
        // Categories list
        <>
          {categories.map(category => renderCategoryItem(category))}
        </>
      )}
    </div>
  );

  return (
    <div className={`solution-categories solution-categories--${layout} ${className}`}>
      {/* Header */}
      <div className="solution-categories__header">
        {layout === 'dropdown' ? (
          renderDropdownTrigger()
        ) : (
          <>
            <h3 className="solution-categories__title">
              Categories
              {showCounts && totalCount > 0 && (
                <span className="solution-categories__total-count">
                  ({totalCount})
                </span>
              )}
            </h3>
            
            {showClearAll && selectedCount > 0 && (
              <button
                className="solution-categories__clear-button"
                onClick={handleClearAll}
                disabled={loading}
              >
                Clear All
              </button>
            )}
          </>
        )}
      </div>

      {/* Categories Content */}
      {layout === 'dropdown' ? (
        <div className={`solution-categories__dropdown ${isDropdownOpen ? 'solution-categories__dropdown--open' : ''}`}>
          <div className="solution-categories__dropdown-content">
            {showClearAll && selectedCount > 0 && (
              <div className="solution-categories__dropdown-header">
                <button
                  className="solution-categories__clear-button"
                  onClick={handleClearAll}
                  disabled={loading}
                >
                  Clear All ({selectedCount})
                </button>
              </div>
            )}
            {renderCategoriesList()}
          </div>
        </div>
      ) : (
        renderCategoriesList()
      )}

      {/* Selected Categories Summary (for horizontal layout) */}
      {layout === 'horizontal' && selectedCount > 0 && (
        <div className="solution-categories__selected-summary">
          <span className="solution-categories__selected-label">
            Filtered by:
          </span>
          <div className="solution-categories__selected-tags">
            {selectedCategories.map(category => (
              <span key={category} className="solution-categories__selected-tag">
                {category}
                <button
                  className="solution-categories__remove-tag"
                  onClick={() => handleCategoryClick(category)}
                  aria-label={`Remove ${category} filter`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SolutionCategories;
